import { h, Fragment } from "preact"
import { useRef, useState } from "preact/hooks"
import { breakpoints } from "../../util/breakpoints"

export const Slider = ({
  elements,
  active,
  type,
  activate,
  data,
  openModalFunction,
  translations,
  fakeSlide,
}) => {
  const currentBreakpoint = breakpoints()

  const [animationClass, setAnimationClass] = useState("")
  const [showFakeSlide, setShowFakeSlide] = useState(
    currentBreakpoint !== "large",
  )

  const activeElementIndex = elements.findIndex((o) =>
    active !== "" ? o.id === active : true,
  )

  const next =
    activeElementIndex < elements.length - 1
      ? elements[activeElementIndex + 1]
      : false
  const previous =
    activeElementIndex > 0 ? elements[activeElementIndex - 1] : false

  const nextFunction = () => {
    if (showFakeSlide) {
      nextAfterFakeSlide()
    } else {
      activate(next.id, type)
      setAnimationClass("from-right")
    }
  }

  const previousFunction = () => {
    if (showFakeSlide) {
      nextAfterFakeSlide()
    } else {
      activate(previous.id, type)
      setAnimationClass("from-left")
    }
  }

  const nextAfterFakeSlide = () => {
    setShowFakeSlide(false)
    activate(next.id, type)
    setAnimationClass("from-right")
  }

  return (
    <div key={type} class={`slider ${animationClass}`}>
      {showFakeSlide || (
        <button
          onClick={previousFunction}
          disabled={!previous}
          class="control-previous"
          aria-label="previous"
        />
      )}
      <div class="slides">
        {data && (
          <Slide
            {...elements[activeElementIndex]}
            {...{
              data,
              next,
              nextFunction,
              previous,
              previousFunction,
              openModalFunction,
              translations,
              currentBreakpoint,
              fakeSlide,
              showFakeSlide,
            }}
          />
        )}
      </div>
      <button
        onClick={nextFunction}
        disabled={!next}
        class="control-next"
        aria-label="next"
      />
    </div>
  )
}

export const Slide = (slide) => {
  const {
    id,
    type,
    data,
    translations,
    currentBreakpoint,
    next,
    previous,
    fakeSlide,
    showFakeSlide,
  } = slide

  const slideRef = useRef(null)
  let dragging = false
  let posX = 0
  let startX = 0

  const touchStart = (e) => {
    dragging = true
    startX = e.targetTouches[0].clientX
    startDragging()
    requestAnimationFrame(positioning)
  }

  const touchEnd = () => {
    // slideRef.current.removeEventListener("touchmove", onDragging)
    posX = 0
    dragging = false
  }

  const startDragging = () => {
    slideRef.current.addEventListener("touchmove", onDragging)
  }

  const onDragging = (e) => {
    posX = e.targetTouches[0].clientX - startX
  }

  const positioning = () => {
    const threshold = slideRef.current.offsetWidth * 0.2
    slideRef.current.style.transform = `translateX(${posX}px)`

    if (next && posX < threshold * -1) {
      touchEnd()
      slide.nextFunction()
    }

    if (previous && posX > threshold) {
      touchEnd()
      slide.previousFunction()
    }

    dragging && requestAnimationFrame(positioning)
  }

  return (
    <>
      {showFakeSlide ? (
        <div
          key={`${type}-fake`}
          class="slide fake-slide"
          ref={slideRef}
          onTouchStart={touchStart}
          onTouchEnd={touchEnd}
        >
          <FakeSlide heading={fakeSlide[0]} text={fakeSlide[1]} />
        </div>
      ) : (
        <div
          key={`${type}-${id}`}
          class="slide"
          ref={slideRef}
          onTouchStart={touchStart}
          onTouchEnd={touchEnd}
        >
          {data && type === "farmers" && currentBreakpoint === "large" ? (
            <FarmerSlider
              {...{ data, translations }}
              breakpoint={currentBreakpoint}
            />
          ) : (
            data && <GeneralSlider {...{ data, slide, type, translations }} />
          )}
        </div>
      )}
    </>
  )
}

const SliderLink = (props) => {
  return (
    <a
      key={`${props.type}-${props.id}`}
      href="#"
      class="slide-link"
      onClick={props.onClick}
    >
      {props.children}
    </a>
  )
}

export const GeneralSlider = ({ data, slide, type, translations }) => {
  return (
    <SliderLink
      id={data.id}
      type={data.type}
      onClick={(e) => {
        e.preventDefault()
        slide.openModalFunction(data.id, data.type)
      }}
    >
      {data.image_slide && <img src={data.image_slide} />}
      {type === "farmers" ? (
        <div class="slide-inner">
          <div class="inner-text">
            <h3>{data.title}</h3>
            {data.village && `${data.village}`}
          </div>

          <h3 class="farmer-name">{data.name}</h3>
        </div>
      ) : (
        <div class="slide-inner">
          <div class="inner-text">
            <h3>{data.title}</h3>
            {data.header_text && `${data.header_text}`}
          </div>
          <h3 class="more-link">{translations.more}</h3>
        </div>
      )}
    </SliderLink>
  )
}

export const FarmerSlider = ({ data, translations, breakpoint }) => {
  const renderImage = (src) => {
    return (
      <div class="image-container">
        <img class="image" src={src} />
      </div>
    )
  }

  return (
    <div key={`slide-farmers-${data.id}`} class="layout--big">
      <div class="header">{data.title}</div>
      {breakpoint === "large" &&
        data.image_medium &&
        renderImage(data.image_medium)}
      {breakpoint !== "large" &&
        data.image_full &&
        renderImage(data.image_full)}
      <div class="details">
        <h3 class="farmer-name">{data.name}</h3>
        {data.village && (
          <p>
            <strong>{translations.village}</strong>
            {data.village}
          </p>
        )}
        {(data.parents !== 0 || data.children !== 0) && (
          <p>
            <strong>{translations.family_members}</strong>
            {data.parents !== 0 && `${data.parents} ${translations.parents}`}
            {data.children !== 0 &&
              `${data.parents !== 0 && ", "}${data.children} ${
                translations.children
              }`}
          </p>
        )}
        {data.total_surface != 0 && (
          <p>
            <strong>{translations.total_surface}</strong>
            {data.total_surface} {translations.ha}
          </p>
        )}
        {data.crops && data.crops.length > 0 && (
          <p>
            <strong>{translations.crops}</strong>
            {data.crops.join(", ")}
          </p>
        )}
        {data.animals && data.animals.length > 0 && (
          <p>
            <strong>{translations.animals}</strong>
            {data.animals.join(", ")}
          </p>
        )}
      </div>
      {data.shop_url && (
        <div class="footer">
          <a href={data.shop_url} class="shop-link">
            {translations.shop}
          </a>
        </div>
      )}
    </div>
  )
}

export const FakeSlide = ({ heading, text }) => {
  return (
    <div class="slide-inner">
      <div class="inner-text">
        <h3>{heading}</h3>
        {text}
      </div>
    </div>
  )
}
